import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, tokenStore } from "@/lib/api";

/**
 * IdleWatch — logs the user out after IDLE_MS of inactivity, on any device.
 *
 * Behaviour:
 *  - Resets on the usual "there's a human here" signals: mouse move, keyboard,
 *    scroll, touch/pointer, wheel.
 *  - Persists the last-activity timestamp in sessionStorage so the timer
 *    survives PWA background/foreground cycles: when the tab becomes visible
 *    again (visibilitychange or focus), we re-check and log out immediately
 *    if the idle window has already elapsed while the phone was locked.
 *  - Redirects to /login?idle=1 so the Login page can display a clear toast.
 *  - Sends a best-effort POST /api/auth/logout so the connection journal
 *    records the event alongside the reason "idle".
 */
const IDLE_MS = 6 * 60 * 1000; // 6 minutes
const CHECK_MS = 15 * 1000;    // re-evaluate at least every 15s
const LAST_ACTIVITY_KEY = "last_activity_at";
const ACTIVITY_EVENTS = [
  "mousemove", "mousedown", "keydown", "wheel",
  "touchstart", "pointerdown", "scroll",
];

export default function IdleWatch() {
  const { user } = useAuth();
  const timerRef = useRef(null);
  const loggingOutRef = useRef(false);

  useEffect(() => {
    if (!user) return; // only arm when someone is authenticated

    const markActive = () => {
      try { sessionStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); }
      catch (_) { /* private mode */ }
    };

    const doLogout = async (reason) => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      try { sessionStorage.removeItem(LAST_ACTIVITY_KEY); } catch (_) {}
      // Best-effort: pass the reason to the journaled logout event.
      try { await api.post("/auth/logout", null, { params: { reason: "idle" } }); }
      catch (_) { /* swallow — we're logging out anyway */ }
      tokenStore.clear();
      // Full navigation so any in-flight requests are aborted along with the app state.
      const target = `/login?idle=1${reason ? `&r=${reason}` : ""}`;
      if (typeof window !== "undefined") window.location.replace(target);
    };

    const check = () => {
      const last = parseInt(sessionStorage.getItem(LAST_ACTIVITY_KEY) || "0", 10);
      if (!last) { markActive(); return; }
      if (Date.now() - last >= IDLE_MS) {
        doLogout("timeout");
      }
    };

    // Seed and arm
    markActive();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    timerRef.current = window.setInterval(check, CHECK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [user]);

  return null;
}
